<template>
  <div class="card-sipe">
    <!-- Titulo y descripcion -->
    <div class="flex align-items-start flex-column">
      <h3 class="card-title">Búsqueda de {{ personTypeLabel }}</h3>
      <p class="p-sipe">
        Ingrese el tipo y número de documento junto a sexo para consultar los datos del
        {{ personTypeLabel }} en el SIPE.
      </p>
    </div>
    <!-- Formulario de busqueda -->
    <div class="grid align-items-center">
      <div class="xl:col-3 hd:col-4 field-container">
        <label for="tipoDocumento" class="labelInput">
          Tipo de identificación
          <span v-if="props.personType === 'affected'" style="color: red">*</span>
        </label>
        <Dropdown
          id="tipoIdentificacion"
          class="input w-full"
          :options="tipoDocumentoList"
          placeholder="Tipo de identificación"
          optionLabel="nombre"
          dataKey="id"
          v-model="documentType"
          :disabled="true"
          test-id="inputDniDenunciante"
          :showClear="!props.isDisable && props.personType !== 'affected'"
        />
        <label v-if="errores.tipoDocumento" class="error-field">
          {{ errores.tipoDocumento }}
        </label>
      </div>

      <div class="xl:col-3 hd:col-3 field-container">
        <label for="documento" class="labelInput">
          Número de Identificación
          <span v-if="props.personType === 'affected'" style="color: red">*</span>
        </label>
        <InputText
          v-no-emoji
          class="input w-full"
          type="text"
          v-model="documentNumber"
          :disabled="props.isDisable"
          placeholder="Número de Identificación"
          test-id="inputDniDenunciante"
        />
        <label
          v-if="modifiedData.documento && errores.documento && !props.isDisable"
          class="error-field"
        >
          {{ errores.documento }}
        </label>
      </div>

      <div class="xl:col-3 hd:col-4 field-container gender">
        <label for="sexo" class="labelInput flex align-items-center">
          Sexo <span v-if="props.personType === 'affected'" style="color: red"> *</span>
        </label>
        <Dropdown
          class="input w-full"
          dataKey="nombre"
          v-model="person.gender"
          placeholder="Sexo"
          :options="sexoList"
          optionLabel="nombre"
          :filter="sexoList?.length >= 5"
          :disabled="props.isDisable"
          ref="genderRef"
        />
      </div>

      <div class="xl:col-2 hd:col-12 mt-4" style="padding: 0">
        <div class="flex">
          <Button
            class="search-button"
            @click="searchOnSipe"
            :disabled="
              documentType === null ||
              documentNumber === null ||
              documentNumber === '' ||
              person.gender === null
            "
            v-if="!loadingSIPESearch"
          >
            <div>
              <Search color="white" :size="20" />
            </div>
          </Button>
          <ProgressSpinner
            style="width: 50px; height: 50px"
            strokeWidth="5"
            fill="#EEEEEE"
            animationDuration="1.8s"
            class="-mt-2 progress-spinner"
            v-if="loadingSIPESearch"
          />
          <Button class="clean-button" @click="cleanSIPEdata" v-if="SIPEsearchUsed">
            <div>
              <BrushCleaning color="#0B5195" :size="20" />
            </div>
            Limpiar
          </Button>
        </div>
      </div>
      <label class="SIPE-error-message">
        {{ SIPEerrorMessage }}
      </label>
    </div>
    <CustomModal
      ref="sipeModalRef"
      :title="sipeModalData.title"
      :message="sipeModalData.message"
      :modalType="sipeModalData.modalType"
      :showSecondaryButton="false"
      :button-labels="['Aceptar']"
      @confirm="closeModalTemplate(sipeModalRef)"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { onMounted } from "vue";
import { useNationalitiesStore } from "@/store/Pinia/useNacionalitiesStore";
import { useInstruccionStore } from "@/store/Pinia/useInstruccionStore";
import { useTypeSexStore } from "@/store/Pinia/useTypeSexStore";
import { useEstadoCivilStore } from "@/store/Pinia/useEstadoCivilStore";
import { useAllConditionsStore } from "@/store/Pinia/useAllConditionsStore";
import { Search, BrushCleaning } from "lucide-vue";
import { useIdentificationTypeStore } from "@/store/Pinia/useIdentificationTypeStore";
import { validateIdentificationByType } from "@/utils/validations/identification";
import CustomModal from "@/components/CustomModal.vue";
import { useValidations } from "@/composables/useValidations";

const { calculateAge } = useValidations();

const sipeModalRef = ref(null);
const sipeModalData = ref({
  title: "",
  message: "",
  modalType: "error",
});

const nationalitiesStore = useNationalitiesStore();
const instruccionStore = useInstruccionStore();
const typeSexStore = useTypeSexStore();
const estadoCivilStore = useEstadoCivilStore();
const conditionsStore = useAllConditionsStore();
const identificationTypeStore = useIdentificationTypeStore();

const props = defineProps({
  personType: { type: String, default: "affected" },
  isDisable: { type: Boolean, default: false },
  personData: { type: Object, default: () => ({}) },
  SIPEsearchUsed: { type: Boolean, default: false },
});

const personTypeLabel = computed(() => {
  return props.personType === "affected" ? "afectado" : "vinculado";
});

const SIPEexample = ref([
  {
    apellido: "DI MARÍA",
    cuil: "",
    domicilios: {
      domicilio: [
        {
          barrio: "",
          calle: "LOS FRESNOS",
          ciudad: "Funes",
          cpostal: "2132",
          departamento: "",
          monoblock: "",
          municipio: "Funes",
          numero: "718",
          pais: "Argentina",
          piso: "",
          provincia: "Santa Fe",
          tipoDomicilio: "Falso",
        },
      ],
    },
    fechaActualiza: "2024-07-25",
    fechaFallecimiento: "",
    fechaNacimiento: "1988-02-14",
    idPersona: "2799684",
    nombre: "Ángel Fabián",
    sexo: "M",
    apodo: "Fideo",
  },
  {
    apellido: "ARANTES DO NASCIMENTO",
    cuil: "",
    domicilios: {
      domicilio: [
        {
          barrio: "",
          calle: "MUITO OBRIGADO",
          ciudad: "Tres Corações",
          cpostal: "1970",
          departamento: "",
          monoblock: "",
          municipio: "Tres Corações",
          numero: "1962",
          pais: "Brasil",
          piso: "",
          provincia: " Minas Gerais",
          tipoDomicilio: "Falso",
        },
      ],
    },
    fechaActualiza: "2025-10-25",
    fechaFallecimiento: "",
    fechaNacimiento: "1940-12-23",
    idPersona: "2799685",
    nombre: "Edson",
    sexo: "M",
    apodo: "Pele",
  },
]);

const emit = defineEmits(["updateFormPersona", "setIsDisabled"]);

onMounted(async () => {
  if (!identificationTypeStore.identificationTypeListLoad) {
    await identificationTypeStore.setIdentificationType();
  }
  const dniDoc = tipoDocumentoList.value.find(
    (tipo) => tipo?.nombre?.toString().toUpperCase() === "DNI"
  );

  if (dniDoc) {
    documentType.value = dniDoc;
    person.tipoDocumento = dniDoc;
  }

  if (props?.personData?.Sexo !== undefined) {
    person.gender = {
      id: typeSexStore.getByNombre(props?.personData?.Sexo),
      nombre: props?.personData?.Sexo,
    };
  }
});

// const tipoDocumentoList = ref([]);
const documentNumberValueRef = ref(null);
const loadingSIPESearch = ref(false);
const documentType = ref(null);
const SIPEerrorMessage = ref("");

const errores = reactive({ email: "" });
const modifiedData = reactive({ documento: false });

const viewData = computed(() => store.getters["sumarioViews/getViewData"]);
const sexoList = computed(() => typeSexStore.sexo);

let person = reactive({
  gender: null,
});

const documentNumber = computed({
  get: () => {
    let value;
    if (documentNumberValueRef.value != null) {
      value = documentNumberValueRef.value;
    } else {
      value = props?.personData?.NumeroIdentificacion
        ? props?.personData?.NumeroIdentificacion
        : "";
    }
    return value;
  },
  set: (value) => {
    documentNumberValueRef.value = value;
  },
});

const tipoDocumentoList = computed(() => {
  return identificationTypeStore.identificationType.map((item) => ({
    id: item.id,
    nombre: item.name,
    descripcion: item.description,
  }));
});
const yesOrNoList = ref([
  { id: 1, nombre: "Sí" },
  { id: 0, nombre: "No" },
]);

async function searchOnSipe() {
  loadingSIPESearch.value = true;
  emit("setIsDisabled", true);
  let data = {};
  try {
    loadingSIPESearch.value = true;
    const sexo = person.gender?.nombre === "Masculino" ? "M" : "F";
    const url = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/data-persona?dni=${documentNumber.value}&sexo=${sexo}`;
    const result = await fetch(url, { credentials: "include" });

    data = await result.json();
    loadingSIPESearch.value = false;
    emit("setIsDisabled", false);
  } catch (e) {
    const env = process.env.VUE_APP_BACKEND_ENVIRONMENT;
    if (
      env === "http://dev.electro3.com.ar/siis/api/v1" ||
      env === "http://localhost/siis/api/v1"
    ) {
      data = {
        sipe: {
          personaInfo: {
            ...SIPEexample.value[Math.round(Math.random())],
          },
          resultadoWS: {
            codigoError: "",
            exito: true,
            msgError: "",
          },
        },
      };
    } else {
      console.error("err", e);
      console.error("Datos incompletos o problema de conexión con SIPE.");
      SIPEerrorMessage.value =
        "El sistema SIPE no está disponible en este momento. Intentá nuevamente más tarde.";

      sipeModalData.value = {
        title: "SIPE no disponible",
        message:
          "El sistema SIPE no está disponible en este momento. Intentá nuevamente más tarde.",
        modalType: "error",
      };
      if (sipeModalRef.value) {
        sipeModalRef.value.open();
      }
    }
  }

  if (data.sipe?.resultadoWS.exito !== true) {
    if (data.sipe && data.sipe.resultadoWS) {
      console.error("Persona no encontrada");
      SIPEerrorMessage.value =
        "No se encontraron resultados para tu búsqueda. Verificá los datos ingresados.";

      sipeModalData.value = {
        title: "Datos no encontrados",
        message: "No se encontraron resultados para tu búsqueda. Verificá los datos ingresados.",
        modalType: "warning",
      };
      if (sipeModalRef.value) {
        sipeModalRef.value.open();
      }
    } else {
      console.error("SIPE sin conexión.");
    }
  } else {
    const dataDeNacimiento = data.sipe.personaInfo.fechaNacimiento;
    const orderedDate =
      dataDeNacimiento.slice(8, 10) +
      dataDeNacimiento.slice(4, 7) +
      "-" +
      dataDeNacimiento.slice(0, 4);

    const yearOldFromSIPE = calculateAge(orderedDate);

    const address = data.sipe.personaInfo.domicilios.domicilio[0];
    const domicile = `${address.calle} ${address.numero}, ${address.barrio} CP:${address.cpostal} - ${address.municipio}, ${address.provincia}, ${address.pais}`;
    const personToUpdate = {
      name: data.sipe.personaInfo.nombre,
      surname: data.sipe.personaInfo.apellido,
      cuil: data.sipe.personaInfo.cuil,
      birthday: orderedDate,
      domicile: domicile,
      yearOld: yearOldFromSIPE,
      documentType: documentType.value,
      documentNumber: documentNumber.value,
      gender: person.gender,
    };
    emit("updateFormPersona", personToUpdate);
  }

  setTimeout(() => {
    loadingSIPESearch.value = false;
    emit("setIsDisabled", false);
    emit("callToSIPEsearchUsed", true);
  }, 5000);
}

const closeModalTemplate = (type) => {
  if (type.value) {
    type.value?.close();
  } else {
    type.close();
  }
};

function cleanSIPEdata() {
  const personToUpdate = {
    name: null,
    surname: null,
    cuil: null,
    birthday: null,
    domicile: null,
    yearOld: null,
  };
  documentNumber.value = "";
  person.gender = null;
  emit("updateFormPersona", personToUpdate);
  emit("callToSIPEsearchUsed", false);
}

watch(documentNumber, (val) => {
  SIPEerrorMessage.value = "";
  modifiedData.documento = true;
  const tieneTipoDocumento = person.tipoDocumento && person.tipoDocumento !== null;
  const esRequerido = props.personType === "affected" || tieneTipoDocumento;
  validateIdentification(val, esRequerido);
  person.documento = val;
  emit("validationStatus", errores);
});

watch(documentType, (val) => {
  SIPEerrorMessage.value = "";
  person.tipoDocumento = val;
  if (!val || val === null) {
    person.documento = "";
    documentNumber.value = "";
    errores.documento = null;
    emit("validationStatus", errores);
    return;
  }
  // Validar documento existente cuando cambia el tipo
  const esRequerido = props.personType === "affected" || (val && val !== null);
  if (person.documento) {
    validateIdentification(person.documento, esRequerido);
  } else if (modifiedData.documento && props.personType === "affected" && !person.documento) {
    errores.documento = "El campo documento es obligatorio.";
  } else if (
    modifiedData.documento &&
    props.personType === "vinculated" &&
    val &&
    val !== null &&
    !person.documento
  ) {
    errores.documento = "El campo documento es obligatorio.";
  } else {
    errores.documento = null;
  }
  emit("validationStatus", errores);
});

function validateIdentification(valor, requerido = true) {
  const tieneTipoDocumento = person.tipoDocumento && person.tipoDocumento !== null;

  // Si es vinculado y no tiene tipo de documento, no validar
  if (props.personType === "vinculated" && !tieneTipoDocumento) {
    errores.documento = null;
    return true;
  }

  // Si es afectado o vinculado con tipo de documento, y es requerido pero está vacío
  if (requerido && !valor) {
    errores.documento = "El campo documento es obligatorio.";
    return false;
  }

  // Si hay tipo de documento y valor, validar formato
  if (tieneTipoDocumento && valor) {
    const { valid, formattedValue, message } = validateIdentificationByType(
      person.tipoDocumento,
      valor
    );

    // Actualizar con el valor formateado si es diferente
    if (formattedValue !== undefined && formattedValue !== person.documento) {
      person.documento = formattedValue;
      documentNumber.value = formattedValue;
    }

    if (!valid) {
      errores.documento = message;
      return false;
    }
  }

  // Si pasó todas las validaciones
  if (
    (props.personType === "affected" && valor) ||
    (props.personType === "vinculated" && tieneTipoDocumento && valor)
  ) {
    errores.documento = null;
  }

  return true;
}
defineExpose({
  person,
  validateIdentification,
  errores,
  cleanSIPEdata,
  searchOnSipe,
  SIPEerrorMessage,
});
</script>

<style scoped>
.card-sipe {
  background-color: #eaeaea80;
  border-radius: 8px;
  margin-bottom: 1rem;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 202px;
}

.field-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.card-title {
  font-size: 24px;
  color: #000000;
  font-weight: 500;
  margin: 0;
}

.p-sipe {
  color: #000000;
  font-weight: 400;
  font-size: 16px;
}

.labelInput {
  color: #0b5195;
  font-size: 16px;
  font-weight: 400;
  font-family: "Inter", sans-serif;
}

.input {
  border: 1px solid #0b5195;
  border-radius: 6px;
  width: 100%;
  color: #9ca3af;
}

.search-button {
  display: flex;
  width: auto;
  height: 36px;
  padding: 12px 15px;
  justify-content: center;
  align-content: center !important;
  flex-shrink: 0;
  border-radius: 6px;
  background: #0b5195;
  color: #ffffff;
  border: 1px solid #0b5195;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
}

.search-button:hover:not(:disabled) {
  background: #0b5195 !important;
  color: #f4f4f4 !important;
  opacity: 0.9;
  cursor: pointer;
}

@media screen and (max-width: 1280px) {
  .hd\:col-12 {
    flex: 0 0 100%;
  }

  .hd\:col-6 {
    flex: 0 0 50%;
  }

  .hd\:col-4 {
    flex: 0 0 33.3333%;
  }

  .hd\:col-3 {
    flex: 0 0 25%;
  }

  .hd\:hidden {
    display: none !important;
  }
  .gender {
    margin-top: 20px;
  }
}
.clean-button {
  display: flex;
  justify-content: center;
  width: 110px;
  height: 36px;
  margin-left: -6.5rem;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--White-White, #fff);
  color: #0b5195;
  border: 1px solid #0b5195;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
  margin-left: 10px;
}

.clean-button:hover {
  background: #f4f4f4 !important;
  color: #0b5195 !important;
  opacity: 0.9;
  cursor: pointer;
}

.progress-spinner {
  margin-left: -1px;
  margin-right: -5px;
}

.error-field {
  color: red;
  display: block;
  margin-top: 70px;
  min-height: 20px;
  position: absolute;
}

.SIPE-error-message {
  color: red;
  margin-top: 10px;
  margin-left: 10px;
}
</style>
